import { useCallback, useMemo, useState } from 'react'
import {
  View,
  Text,
  Pressable,
  ScrollView,
  Platform,
} from 'react-native'
import { useTheme } from '../../../theme/useTheme'
import { useThemeContext } from '../../../theme/ThemeContext'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { B2B_INDUSTRIES, B2B_INDUSTRY_LABELS } from '../../constants/b2bIndustry.constants'
import { sanitizeInput, sanitizeWebsiteUrlInput } from '../../../helpers/sanitizeInput.helper'
import {
  getRegistrationFormStyles,
  registrationFormPlaceholderColor,
} from './AccountRegistrationForm.styles'
import { OrgFormTextInput } from './OrgFormTextInput'
import { OrgFormTextArea } from './OrgFormTextArea'
import { ORG_REGISTRATION_FORM_COPY } from './AccountRegistrationForm.constants'
import { ACCOUNT_REGISTRATION_FORM_TEST_IDS } from '../../constants/accountRegistrationForm.constants'
import {
  validateOrgCreatePayload,
} from '../../helpers/orgFormValidation.helper'
import { isValidE164OrgPhone } from '../../helpers/orgPhoneValidation.helper'
import {
  ORG_DESCRIPTION_MAX_LENGTH,
  ORG_DISPLAY_NAME_MAX_LENGTH,
  ORG_NAME_MAX_LENGTH,
  ORG_WEBSITE_URL_MAX_LENGTH,
} from '../../constants/orgForm.constants'

import type { B2bAccountCreatePayload } from '../../types/b2bAccount.types'
import type { B2bIndustry } from '../../constants/b2bIndustry.constants'

interface AccountRegistrationFormProps {
  onSubmitCreate: (payload: B2bAccountCreatePayload) => Promise<void>
  onSubmitJoin: (_joinCode: string) => Promise<void>
  busy: boolean
  error: string | null
  initialJoinCode?: string
  /** Email of the signed-in creator — used as contact email without showing a separate field. */
  creatorEmail?: string
  /** Marketing site shell owns page scroll/card and uses a forced light visual treatment. */
  marketing?: boolean
  embedInOuterScroll?: boolean
}

export function AccountRegistrationForm({
  onSubmitCreate,
  onSubmitJoin: _onSubmitJoin,
  busy,
  error,
  creatorEmail = '',
  marketing = false,
  embedInOuterScroll = false,
}: AccountRegistrationFormProps) {
  const theme = useTheme()
  const { isDark } = useThemeContext()
  const styles = useMemo(
    () => getRegistrationFormStyles(theme, { marketing, isDark }),
    [isDark, marketing, theme],
  )
  const placeholderColor = useMemo(
    () => registrationFormPlaceholderColor(theme, isDark),
    [isDark, theme],
  )

  const [name, setName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [industry, setIndustry] = useState<B2bIndustry | ''>('')
  const [description, setDescription] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)

  const handleCreate = useCallback(async () => {
    setLocalError(null)
    const validation = validateOrgCreatePayload({
      name,
      displayName,
      description,
      contactEmail: creatorEmail,
      contactPhone,
      websiteUrl,
      primaryColor: '',
      secondaryColor: '',
    })
    if (!validation.ok || !validation.value) {
      setLocalError(validation.error ?? ORG_REGISTRATION_FORM_COPY.ERROR_FALLBACK_VALIDATION)
      return
    }

    const payload: B2bAccountCreatePayload = validation.value
    if (industry) payload.industry = industry
    await onSubmitCreate(payload)
  }, [name, displayName, industry, description, creatorEmail, contactPhone, websiteUrl, onSubmitCreate])

  const canCreate =
    name.trim().length >= 2 &&
    (contactPhone.trim() === '' || isValidE164OrgPhone(contactPhone))

  const errorMessage = localError ?? error
  const errorEl = errorMessage ? <Text style={styles.errorText}>{errorMessage}</Text> : null

  const organizationFields = (
    <>
      <Text style={styles.label}>{ORG_REGISTRATION_FORM_COPY.ORG_NAME_LABEL}</Text>
      <OrgFormTextInput
        marketing={marketing}
        styles={styles}
        value={name}
        onChangeText={(text) => {
          setLocalError(null)
          setName(sanitizeInput(text, true).slice(0, ORG_NAME_MAX_LENGTH))
        }}
        placeholder={ORG_REGISTRATION_FORM_COPY.ORG_NAME_PLACEHOLDER}
        placeholderTextColor={placeholderColor}
      />

      <Text style={styles.label}>{ORG_REGISTRATION_FORM_COPY.DISPLAY_NAME_LABEL}</Text>
      <OrgFormTextInput
        marketing={marketing}
        styles={styles}
        value={displayName}
        onChangeText={(text) => {
          setLocalError(null)
          setDisplayName(sanitizeInput(text, true).slice(0, ORG_DISPLAY_NAME_MAX_LENGTH))
        }}
        placeholder={ORG_REGISTRATION_FORM_COPY.DISPLAY_NAME_PLACEHOLDER}
        placeholderTextColor={placeholderColor}
      />

      <Text style={styles.label}>{ORG_REGISTRATION_FORM_COPY.INDUSTRY_LABEL}</Text>
      <View style={styles.pickerRow}>
        {B2B_INDUSTRIES.map((ind) => (
          <Pressable
            key={ind}
            accessibilityRole="button"
            onPress={() => setIndustry(industry === ind ? '' : ind)}
            style={({ pressed }) => [
              styles.pickerChip,
              industry === ind && styles.pickerChipActive,
              pressed && styles.pressed,
            ]}
          >
            <Text
              style={[
                styles.pickerChipText,
                industry === ind && styles.pickerChipTextActive,
              ]}
            >
              {B2B_INDUSTRY_LABELS[ind]}
            </Text>
          </Pressable>
        ))}
      </View>

      <Text style={styles.label}>{ORG_REGISTRATION_FORM_COPY.DESCRIPTION_LABEL}</Text>
      <OrgFormTextArea
        marketing={marketing}
        styles={styles}
        value={description}
        onChangeText={(text) => {
          setLocalError(null)
          setDescription(sanitizeInput(text, true).slice(0, ORG_DESCRIPTION_MAX_LENGTH))
        }}
        placeholder={ORG_REGISTRATION_FORM_COPY.DESCRIPTION_PLACEHOLDER}
        placeholderTextColor={placeholderColor}
      />

      <View style={styles.divider} />

      <Text style={styles.label}>{ORG_REGISTRATION_FORM_COPY.WEBSITE_LABEL}</Text>
      <OrgFormTextInput
        marketing={marketing}
        styles={styles}
        value={websiteUrl}
        onChangeText={(text) => {
          setLocalError(null)
          setWebsiteUrl(sanitizeWebsiteUrlInput(text).slice(0, ORG_WEBSITE_URL_MAX_LENGTH))
        }}
        placeholder={ORG_REGISTRATION_FORM_COPY.WEBSITE_PLACEHOLDER}
        placeholderTextColor={placeholderColor}
        autoCapitalize="none"
      />

      <Text style={styles.label}>{ORG_REGISTRATION_FORM_COPY.PHONE_LABEL}</Text>
      <OrgFormTextInput
        marketing={marketing}
        styles={styles}
        testID={ACCOUNT_REGISTRATION_FORM_TEST_IDS.PHONE_INPUT}
        value={contactPhone}
        onChangeText={(text) => {
          setLocalError(null)
          setContactPhone(text.replace(/[^\d+\s\-().]/g, ''))
        }}
        placeholder={ORG_REGISTRATION_FORM_COPY.PHONE_PLACEHOLDER}
        placeholderTextColor={placeholderColor}
        keyboardType="phone-pad"
        autoCapitalize="none"
      />

    </>
  )

  const createSubmit = (
    <View style={styles.submitRow}>
      <Pressable
        accessibilityRole="button"
        testID={ACCOUNT_REGISTRATION_FORM_TEST_IDS.SUBMIT_BUTTON}
        disabled={busy || !canCreate}
        onPress={() => void handleCreate()}
        style={({ pressed }) => [
          styles.primaryBtn,
          (busy || !canCreate) && styles.primaryBtnDisabled,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.pressed,
        ]}
      >
        <Text style={styles.primaryBtnText}>{ORG_REGISTRATION_FORM_COPY.SUBMIT_BUTTON}</Text>
      </Pressable>
    </View>
  )

  const formContent = (
    <>
      <Text style={styles.sectionTitle}>{ORG_REGISTRATION_FORM_COPY.SECTION_TITLE}</Text>
      {organizationFields}
      {errorEl}
      {createSubmit}
    </>
  )

  if (embedInOuterScroll) {
    return <View style={styles.container}>{formContent}</View>
  }

  return (
    <ScrollView contentContainerStyle={styles.container}>
      {formContent}
    </ScrollView>
  )
}
